import { Button } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { Formik, FormikHelpers } from 'formik';
import { useCountdown, useMutateOptions } from 'hooks';
import i18next, { t } from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect } from 'react';
import { PiPaperPlaneTilt, PiShieldPlus, PiTimer } from 'react-icons/pi';
import { useNavigate, useParams } from 'react-router-dom';
import { useLoginTwoFactor, useResendTwoFactor } from 'services/auth/twoFactor';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import * as Yup from 'yup';

type FormValues = {
  email: string;
  code: string;
  token: string;
};

const TwoFactor = () => {
  const { code, token, email } = useParams();
  const { twoFactorTokenExpiresAt, twoFactorMethod } = useStoreState();
  const navigate = useNavigate();
  const loginTwoFactor = useLoginTwoFactor();
  const resendTwoFactor = useResendTwoFactor();
  const mutateOptions = useMutateOptions<FormValues>();

  const { countdown, isCountingDown, startCounter } = useCountdown(60);

  const isOtp = twoFactorMethod === 'otp';

  const i18nPrefix = isOtp ? 'twoFactor.otp' : 'twoFactor';

  const onGoBackToLogin = () => navigate(`/login?email=${email}`, { replace: true });

  const onLoginTwoFactor = async (body: FormValues, helpers?: FormikHelpers<FormValues>) => {
    await loginTwoFactor.mutateAsync(body, helpers && mutateOptions(helpers));
  };
  console.log('twoFactorTokenExpiresAt:', twoFactorTokenExpiresAt);
  const onResendTwoFactor = async (email: string, token: string) => {
    await resendTwoFactor.mutateAsync(
      { email, token },
      { onSuccess: startCounter, onError: onGoBackToLogin },
    );
  };

  useEffect(() => {
    if (code && token && email) {
      onLoginTwoFactor({ code, token, email });
    }
    if (!token || !twoFactorTokenExpiresAt) {
      onGoBackToLogin();
    }
    if (token && twoFactorTokenExpiresAt) {
      const timeout = twoFactorTokenExpiresAt - 60000 - Date.now();

      console.log(`Two Factor Token expires in: ${Math.round(timeout / 60000)} min`);

      const timerId = setTimeout(() => onGoBackToLogin(), timeout);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [code, token, email]);

  const initialValues: FormValues = {
    email: email || '',
    code: code || '',
    token: token || '',
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(i18next.t('formik-error.required')),
    token: Yup.string(),
    email: Yup.string(),
  });

  return (
    <PublicPage
      header={
        <>
          <PiShieldPlus />
          {t(`${i18nPrefix}.title`)}
        </>
      }
      footer={
        isOtp ? (
          <div className="flex flex-col items-center gap-2 w-full text-center">
            {' '}
            <Button
              variant={BUTTON_VARIANT.white}
              onClick={() => navigate(`/two-factor-recovery?email=${email}&token=${token}`)}
            >
              <Button.Text text={t('twoFactorRecovery.button')} />
            </Button>
          </div>
        ) : code ? undefined : (
          <div className="flex flex-col items-center gap-2 w-full text-center">
            {t('twoFactor.footer')}
            <Button
              variant={BUTTON_VARIANT.white}
              disabled={isCountingDown || !email || !token}
              onClick={() => email && token && onResendTwoFactor(email, token)}
              isLoading={resendTwoFactor.isPending}
              isClear
            >
              <Button.Text
                text={
                  isCountingDown
                    ? t('button.resend-timer', { timer: countdown })
                    : t('button.resend')
                }
              />
              <Button.Icon icon={isCountingDown ? <PiTimer /> : <PiPaperPlaneTilt />} />
            </Button>

            <Button
              variant={BUTTON_VARIANT.white}
              onClick={() => navigate(`/two-factor-recovery?email=${email}&token=${token}`)}
            >
              <Button.Text text={t('twoFactorRecovery.button')} />
            </Button>
          </div>
        )
      }
    >
      <div className="w-72 opacity-65">{t(`${i18nPrefix}.message`)}</div>
      <Formik
        validateOnBlur
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={onLoginTwoFactor}
      >
        {({ values, touched, errors, handleChange, submitForm, isValid }) => (
          <div>
            <FormInput.Text
              name="code"
              type="text"
              placeholder={'000 000'}
              maxLength={6}
              label={t('twoFactor.code')}
              value={values.code}
              isError={!isValid}
              error={touched.code ? errors.code : undefined}
              onChange={handleChange('code')}
              borderless
              appBackground
            />
            <br />
            <div>
              <Button
                isLoading={loginTwoFactor.isPending}
                onClick={submitForm}
                type="submit"
                variant={BUTTON_VARIANT.submit}
                fullWidth
              >
                <Button.Text text={t('twoFactor.button')} />
              </Button>
            </div>
          </div>
        )}
      </Formik>
    </PublicPage>
  );
};

export default TwoFactor;
